import ExcelJS from 'exceljs'
import { isSafeDatasheetUrl, type BrokerRequestDetailDto } from '@crm/shared'

/**
 * Файл запроса брокеру по нетарифке (Л2, docs/features/nontariff.md).
 *
 * Один файл = один запрос брокеру = уникальные партномера. Колонки — в порядке
 * выгрузки программы брокера (с 29.10.2026): Артикул, Производитель, Описание,
 * Код ТНВД, Повышенный сбор, Пошлина, Нетарифка, Сертификат, Особенности; наш
 * «Даташит» — последней колонкой. Без цветной подсветки. Код запроса лежит в
 * свойствах документа (`subject = broker-request:<код>`): если брокер ответил в
 * нашем файле, «Загрузить ответ» сам находит запрос.
 *
 * «Скачать с ответами» — тот же файл, где колонки брокера заполнены записанными
 * ответами в его формате (сбор — «Да[73860.00]» / «Нет»).
 */

export const BROKER_SHEET_NAME = 'Нетарифка'
export const BROKER_SUBJECT_PREFIX = 'broker-request:'

/** Заголовки ровно в этом порядке: так выгружает брокер, парсер ответа ищет колонки по ним. */
export const BROKER_FILE_HEADERS = [
  'Артикул',
  'Производитель',
  'Описание',
  'Код ТНВД',
  'Повышенный сбор',
  'Пошлина',
  'Нетарифка',
  'Сертификат',
  'Особенности',
  'Даташит',
] as const

const COL_TNVED = 4
const COL_DATASHEET = 10
const COLUMN_WIDTHS = [24, 20, 32, 16, 18, 10, 42, 30, 42, 40]

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const border: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
}
const headerFont: Partial<ExcelJS.Font> = { name: 'Calibri', size: 11, bold: true }
const bodyFont: Partial<ExcelJS.Font> = { name: 'Calibri', size: 11 }
const linkFont: Partial<ExcelJS.Font> = {
  ...bodyFont,
  color: { argb: 'FF0563C1' },
  underline: true,
}

export type BrokerFileSource = Pick<BrokerRequestDetailDto, 'fileCode' | 'items'>

export function brokerFileName(fileCode: string): string {
  return `${fileCode}.xlsx`
}

/** Файл остатков — партномера без ответа; код запроса внутри тот же. */
export function brokerRemainderFileName(fileCode: string): string {
  return `${fileCode}-остатки.xlsx`
}

/** Файл с записанными ответами брокера (имя латиницей: кириллицу в имени blob-файла Chromium иногда теряет). */
export function brokerAnswersFileName(fileCode: string): string {
  return `${fileCode}-answers.xlsx`
}

/** Сбор в формате брокера: «Да[73860.00]» / «Нет»; не ответил — пусто. */
export function brokerFeeText(fee: number | null | undefined): string | null {
  if (fee == null) return null
  return fee > 0 ? `Да[${fee.toFixed(2)}]` : 'Нет'
}

export interface BuildBrokerWorkbookOptions {
  /** Заполнить колонки брокера записанными ответами («Скачать с ответами»). */
  withAnswers?: boolean
}

/** Книга Excel запроса брокеру (без скачивания — для тестов и повторного скачивания). */
export function buildBrokerWorkbook(
  detail: BrokerFileSource,
  options: BuildBrokerWorkbookOptions = {},
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook()
  wb.title = detail.fileCode
  wb.subject = `${BROKER_SUBJECT_PREFIX}${detail.fileCode}`
  wb.creator = 'micro-hub CRM'

  const ws = wb.addWorksheet(BROKER_SHEET_NAME, {
    views: [{ state: 'frozen', ySplit: 1 }],
  })
  ws.columns = COLUMN_WIDTHS.map((width) => ({ width }))

  const header = ws.getRow(1)
  BROKER_FILE_HEADERS.forEach((title, i) => {
    const cell = header.getCell(i + 1)
    cell.value = title
    cell.font = headerFont
    cell.border = border
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  })
  header.height = 22

  detail.items.forEach((item, idx) => {
    const row = ws.getRow(idx + 2)
    const a = options.withAnswers ? item.answer : null
    // Пустые ячейки — null (а не ''), чтобы в файле они были действительно пустыми.
    const values: ExcelJS.CellValue[] = [
      item.partNumber,
      item.brand || null,
      a?.description || item.description || null,
      a?.tnvedCode || item.tnvedCode || null,
      brokerFeeText(a?.customsFee),
      a?.dutyRate ?? null,
      a?.nonTariffNote || null,
      a?.certificateNote || null,
      a?.featuresNote || null,
      null, // Даташит — ниже, гиперссылкой
    ]
    for (let col = 1; col <= BROKER_FILE_HEADERS.length; col++) {
      const cell = row.getCell(col)
      cell.value = values[col - 1] ?? null
      cell.font = bodyFont
      cell.border = border
      cell.alignment = { vertical: 'top', wrapText: true }
    }
    // Код ТН ВЭД — текстом: иначе Excel съест ведущий ноль и покажет 8,54E+09.
    row.getCell(COL_TNVED).numFmt = '@'
    const url = item.datasheetUrl
    if (isSafeDatasheetUrl(url)) {
      const cell = row.getCell(COL_DATASHEET)
      cell.value = { text: url, hyperlink: url }
      cell.font = linkFont
    }
  })

  return wb
}

/** Скачать книгу файлом через временную ссылку (как остальные Excel-выгрузки сайта). */
export async function saveWorkbookAsFile(wb: ExcelJS.Workbook, fileName: string): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: XLSX_MIME })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
